/**
 * Ambient occlusion settings, in their own module so that the tuning store can
 * seed itself from them without importing the post-processing component.
 */

export const AMBIENT_OCCLUSION = {
  /**
   * Occlusion reach, in metres. Tune it together with `samples`: GTAO walks
   * `STEPS` probes per direction at
   * `radius * ((j + 1) / STEPS) ^ distanceExponent`. The current noise texture
   * has alpha fixed at 1, so these distances are not radially jittered.
   *
   * At `samples: 16`, three directions with six probes each put the ladder at
   * 1.39 / 5.56 / 12.5 / 22.2 / 34.7 / 50 mm. Changing the sample count changes
   * both the angular coverage and the probe ladder.
   */
  radius: 0.05,
  /**
   * Darkening curve, applied as `ao = pow(ao, scale)`. This is the strength
   * dial: it deepens occluded areas while leaving open surfaces at 1, so it
   * cannot crush or clip the way a `blendIntensity` above 1 would.
   */
  scale: 4,
  /**
   * How deep a surface is assumed to be, in metres. GTAO defaults this to 1 m,
   * which makes every thin panel occlude as if it were a solid metre-thick
   * block — a case shell is ~10 mm.
   */
  thickness: 0.05,
  distanceExponent: 2,
  /**
   * This is a full-resolution loop over every pixel every frame, so it is the
   * most expensive number here — but it cannot be cut on cost alone.
   *
   * It sets both angular coverage and probes per direction. With a quadratic
   * `distanceExponent`, fewer probes move the first sample farther from the
   * surface pixel and can miss millimetre-scale seams. Tune it with `radius`.
   */
  samples: 16,
  intensity: 1,
} as const;

/** GTAO's edge-aware Poisson denoiser. */
export const GTAO_DENOISE = {
  /** Kernel radius, in pixels. */
  radius: 8,
  rings: 3,
  radiusExponent: 1,
  lumaPhi: 10,
  /** Depth weighting scale, in metres. */
  depthPhi: 0.02,
  normalPhi: 8,
  passes: 2,
} as const;

/**
 * The dials a developer can move at runtime. Deliberately smaller than the
 * pass's full configuration: these are the ones that change what the image
 * looks like rather than how it is computed.
 */
export interface AmbientOcclusionTuning {
  /** Occlusion reach, in metres. */
  radius: number;
  /** Darkening curve, applied as `pow(ao, intensity)`. */
  intensity: number;
  /** Probes per pixel. Changing it recompiles the AO shader. */
  samples: number;
  /** Denoise kernel size, in pixels. */
  denoiseRadius: number;
}

export const DEFAULT_AMBIENT_OCCLUSION_TUNING: AmbientOcclusionTuning = {
  radius: AMBIENT_OCCLUSION.radius,
  intensity: AMBIENT_OCCLUSION.scale,
  samples: AMBIENT_OCCLUSION.samples,
  denoiseRadius: GTAO_DENOISE.radius,
};
